/**
 * Layout-kolom met toggle-knoppen voor de vijf side-panels (M6 Explorer,
 * Concepten, Types, Diagrammen, Relaties). Rendert links in de row-flex
 * vóór TypePalette; verschijnt alleen wanneer minstens één paneel
 * gesloten is, anders neemt 'ie geen ruimte in (return null).
 *
 * Per paneel is de open-state een eigen useUi-flag. Klik op een knop
 * zet dat paneel open. Eerder gedrag was een absolute-positioned floater
 * binnen `<main>` — dat overlapte content (vooral de fixed-position
 * BMC-canvas-grid). Sinds 2026-05-10 onderdeel van de layout-row zodat
 * 't nooit over canvas of paneel-content valt.
 */
import { useTranslation } from "react-i18next";
import { useUi } from "../state/ui";

export function PanelLauncher() {
  const { t } = useTranslation();
  const conceptOpen = useUi((s) => s.conceptExplorerOpen);
  const setConceptOpen = useUi((s) => s.setConceptExplorerOpen);
  const m6ExplorerOpen = useUi((s) => s.explorerOpen);
  const setM6ExplorerOpen = useUi((s) => s.setExplorerOpen);
  const typeOpen = useUi((s) => s.typePaletteOpen);
  const setTypeOpen = useUi((s) => s.setTypePaletteOpen);
  const diagramsOpen = useUi((s) => s.diagramsPanelOpen);
  const setDiagramsOpen = useUi((s) => s.setDiagramsPanelOpen);
  const relationsOpen = useUi((s) => s.relationsPanelOpen);
  const setRelationsOpen = useUi((s) => s.setRelationsPanelOpen);

  const items: Array<{ id: string; label: string; open: boolean; setOpen: (b: boolean) => void }> =
    [
      {
        id: "explorer",
        label: t("app.panels.explorer"),
        open: m6ExplorerOpen,
        setOpen: setM6ExplorerOpen,
      },
      {
        id: "concepts",
        label: t("app.panels.concepts"),
        open: conceptOpen,
        setOpen: setConceptOpen,
      },
      { id: "types", label: t("app.panels.types"), open: typeOpen, setOpen: setTypeOpen },
      {
        id: "diagrams",
        label: t("app.panels.diagrams"),
        open: diagramsOpen,
        setOpen: setDiagramsOpen,
      },
      {
        id: "relations",
        label: t("app.panels.relations"),
        open: relationsOpen,
        setOpen: setRelationsOpen,
      },
    ];
  const closed = items.filter((i) => !i.open);
  if (closed.length === 0) return null;
  return (
    <aside className="ledger flex h-full shrink-0 flex-col gap-1 border-(--ld-control) border-r bg-(--ld-raised) p-2">
      {closed.map((p) => (
        <button
          key={p.id}
          type="button"
          className="rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-left text-(--ld-text) text-xs hover:border-(--ld-text) hover:bg-(--ld-active)"
          onClick={() => p.setOpen(true)}
          title={t("app.panels.openPanelTitle", { label: p.label })}
        >
          ☰ {p.label}
        </button>
      ))}
    </aside>
  );
}
